// src/utils/eventNamingHelper.js
// Helper functions for implementing correct event and streaming title naming
//
// PUBLISHER COMPLIANCE (Capcom Community License, etc.):
// - Event names must NOT include game names
// - ✅ "Dojo Duel $18 to 1st" (event name - shown in UI)
// - ✅ "STREET FIGHTER 6" (game label - shown separately via GAME_PROFILES)
// - ❌ "Street Fighter 6 Dojo Duel $18 to 1st" (violates licensing)
//
// Stream titles CAN include game abbreviation for Twitch/YouTube categorization:
// - ✅ "CritCash SF6 Dojo Duel $18 to 1st"

import { GAME_PROFILES } from '../constants/gameData';
import {
  getFightingBracketMeta,
  getSportsBracketMeta,
  getMobaBracketMeta,
  getShooterBracketMeta,
  getRtsBracketMeta,
} from '../constants/gameMeta';
import {
  makeEventDisplayName,
  makeBaseStreamTitle,
  makeEntryCode,
  composePerUserTitle,
} from './streamTitle';

/**
 * Get bracket metadata based on game type
 */
function getBracketMeta(gameKey, bracketSize) {
  const gameProfile = GAME_PROFILES[gameKey];
  if (!gameProfile) return null;

  const type = gameProfile.type;
  
  switch (type) {
    case 'fighting':
      return getFightingBracketMeta(gameKey, bracketSize);
    case 'sports':
      return getSportsBracketMeta(gameKey, bracketSize);
    case 'moba':
      return getMobaBracketMeta(gameKey, bracketSize);
    case 'shooter':
      return getShooterBracketMeta(gameKey, bracketSize);
    case 'rts':
      return getRtsBracketMeta(gameKey, bracketSize);
    default:
      return null;
  }
}

/**
 * Generate complete event naming data for event creation
 * 
 * PUBLISHER COMPLIANCE: Event names do NOT include game names.
 * Game name is displayed separately in UI via GAME_PROFILES[gameKey].displayName
 * 
 * This function should be called when creating a new event.
 * It returns both the display name and base streaming title.
 * 
 * @param {Object} params
 * @param {string} params.gameKey - Game key from GAME_PROFILES (e.g., 'sf6')
 * @param {number} params.bracketSize - Number of entrants/teams
 * @param {number} params.firstPlacePrizeCents - First place prize in cents
 * @returns {Object} { eventName, baseStreamTitle, bracketTypeName } or null if invalid
 * 
 * @example
 * generateEventNames({ gameKey: 'sf6', bracketSize: 4, firstPlacePrizeCents: 1800 })
 * // Returns:
 * // {
 * //   eventName: "Dojo Duel $18 to 1st",              // NO game name
 * //   baseStreamTitle: "CritCash SF6 Dojo Duel $18 to 1st",  // Has game abbrev
 * //   bracketTypeName: "Dojo Duel"
 * // }
 */
export function generateEventNames({ gameKey, bracketSize, firstPlacePrizeCents, isCrits = false }) {
  // Validate inputs
  if (!gameKey || !bracketSize || firstPlacePrizeCents === undefined) {
    console.warn('generateEventNames: Missing required parameters');
    return null;
  }

  // Get game profile
  const gameProfile = GAME_PROFILES[gameKey];
  if (!gameProfile) {
    console.warn(`generateEventNames: Unknown game key: ${gameKey}`);
    return null;
  }

  // Get bracket metadata
  const bracketMeta = getBracketMeta(gameKey, bracketSize);
  if (!bracketMeta) {
    console.warn(`generateEventNames: No bracket meta found for ${gameKey} size ${bracketSize}`);
    return null;
  }

  // Generate event display name WITHOUT game name (publisher compliant)
  // Format: "Dojo Duel $18 to 1st"
  // Game name is displayed separately in UI via GAME_PROFILES[gameKey].displayName
  const eventName = makeEventDisplayName({
    bracketTypeName: bracketMeta.name,
    firstPlacePrizeCents: firstPlacePrizeCents,
    isCrits,
  });

  // Generate base streaming title WITH game abbreviation (OK for streams)
  // Format: "CritCash SF6 Dojo Duel $18 to 1st"
  const baseStreamTitle = makeBaseStreamTitle({
    brand: 'CritCash',
    gameDisplayName: gameProfile.displayName,
    gameAbbreviation: gameProfile.abbr,
    bracketTypeName: bracketMeta.name,
    firstPlacePrizeCents: firstPlacePrizeCents,
    isCrits,
  });

  return {
    eventName,           // For display in lobby and event detail (NO game name)
    baseStreamTitle,     // For storage on event document (has game abbrev)
    bracketTypeName: bracketMeta.name,
  };
}

/**
 * Generate user-specific streaming title
 * 
 * This should be called server-side when a user joins an event.
 * The result is saved to the user's entry document.
 * 
 * @param {Object} params
 * @param {string} params.baseStreamTitle - From event.requiredStreamTitle
 * @param {string} params.eventId - Event document ID
 * @param {string} params.userId - User's UID
 * @returns {Object} { streamCode, streamingTitle }
 */
export function generateUserStreamTitle({ baseStreamTitle, eventId, userId }) {
  if (!baseStreamTitle || !eventId || !userId) {
    console.warn('generateUserStreamTitle: Missing required parameters');
    return null;
  }

  // Generate unique code
  const streamCode = makeEntryCode(eventId, userId);

  // Append code to base title
  const streamingTitle = composePerUserTitle(baseStreamTitle, streamCode);

  return {
    streamCode,      // Store for reference
    streamingTitle,  // Full title user must use
  };
}

/**
 * IMPLEMENTATION GUIDE
 * 
 * ============================================================================
 * PUBLISHER COMPLIANCE RULES
 * ============================================================================
 * 
 * Per Capcom Community License (and similar publisher guidelines):
 * 
 * ❌ WRONG: "Street Fighter 6 Dojo Duel $18 to 1st"
 *    - Game name incorporated into event name
 *    - Violates naming restrictions
 * 
 * ✅ CORRECT:
 *    - Game Label (separate):  "STREET FIGHTER 6"
 *    - Event Name (yours):     "Dojo Duel $18 to 1st"
 *    - Stream Title:           "CritCash SF6 Dojo Duel $18 to 1st"
 *                              (abbreviation OK for streaming categorization)
 * 
 * ============================================================================
 * 1. IN EVENT CREATION (CreateEventsScreen.js or Cloud Function)
 * ============================================================================
 * 
 * When creating a new event, use generateEventNames():
 * 
 * ```javascript
 * import { generateEventNames } from '../utils/eventNamingHelper';
 * 
 * // Calculate first place prize
 * const entryFeeCents = 500; // $5.00
 * const entriesExpected = 4; // 4 entrants
 * const rakePercent = 10;
 * const grossPoolCents = entryFeeCents * entriesExpected;
 * const netPoolCents = grossPoolCents * (1 - rakePercent / 100);
 * const firstPlacePrizeCents = Math.floor(netPoolCents); // Winner takes all for example
 * 
 * // Generate names
 * const naming = generateEventNames({
 *   gameKey: 'sf6',
 *   bracketSize: 4,
 *   firstPlacePrizeCents: firstPlacePrizeCents,
 * });
 * 
 * if (!naming) {
 *   throw new Error('Could not generate event names');
 * }
 * 
 * // Create event with proper names
 * const eventData = {
 *   name: naming.eventName,                      // "Dojo Duel $18 to 1st" (NO game name)
 *   requiredStreamTitle: naming.baseStreamTitle, // "CritCash SF6 Dojo Duel $18 to 1st"
 *   game: 'sf6',                                 // Game key for GAME_PROFILES lookup
 *   bracketSize: 4,
 *   entryFeeCents: 500,
 *   entriesExpected: 4,
 *   // ... other fields
 * };
 * 
 * await addDoc(collection(db, 'tournaments'), eventData);
 * ```
 * 
 * ============================================================================
 * 2. IN USER ENTRY (Server-side Cloud Function)
 * ============================================================================
 * 
 * When a user joins, generate their unique streaming title:
 * 
 * ```javascript
 * import { generateUserStreamTitle } from '../utils/eventNamingHelper';
 * 
 * // Get event data
 * const eventDoc = await getDoc(doc(db, 'tournaments', eventId));
 * const event = eventDoc.data();
 * 
 * // Generate user's unique streaming title
 * const userStream = generateUserStreamTitle({
 *   baseStreamTitle: event.requiredStreamTitle, // "CritCash SF6 Dojo Duel $18 to 1st"
 *   eventId: eventId,                           // "abc123xyz789"
 *   userId: userId,                             // "user456def"
 * });
 * 
 * if (!userStream) {
 *   throw new Error('Could not generate user streaming title');
 * }
 * 
 * // Save to user's entry
 * const entryData = {
 *   userId: userId,
 *   streamCode: userStream.streamCode,           // "BC123X-USER45"
 *   streamingTitle: userStream.streamingTitle,   // "CritCash SF6 Dojo Duel $18 to 1st • BC123X-USER45"
 *   joinedAt: serverTimestamp(),
 *   isStreaming: false,
 *   streamPlatform: null,
 *   // ... other fields
 * };
 * 
 * await setDoc(doc(db, 'tournaments', eventId, 'entries', userId), entryData);
 * 
 * // Return streaming title to user
 * return { success: true, streamingTitle: userStream.streamingTitle };
 * ```
 * 
 * ============================================================================
 * 3. IN LOBBY SCREEN (Display event names)
 * ============================================================================
 * 
 * Display game name and event name SEPARATELY:
 * 
 * ```javascript
 * // In LobbyScreen.js
 * import { GAME_PROFILES } from '../constants/gameData';
 * 
 * // Game label (from GAME_PROFILES)
 * <Text style={styles.gameLabel}>
 *   {GAME_PROFILES[event.game]?.displayName}
 * </Text>
 * // Renders: "STREET FIGHTER 6"
 * 
 * // Event name (from event.name - NO game name)
 * <Text style={styles.eventTitle}>
 *   {event.name}
 * </Text>
 * // Renders: "Dojo Duel $18 to 1st"
 * ```
 * 
 * ============================================================================
 * 4. IN EVENT DETAIL SCREEN (Display event name and streaming title)
 * ============================================================================
 * 
 * Display game and event name separately at the top:
 * 
 * ```javascript
 * // In EventDetailScreen.js
 * <Text style={styles.gameLabel}>
 *   {GAME_PROFILES[event.game]?.displayName}
 * </Text>
 * <Text style={styles.eventTitle}>
 *   {event.name}
 * </Text>
 * ```
 * 
 * Show user their streaming title after they join:
 * 
 * ```javascript
 * import { StreamTitleDisplay } from '../utils/getUserStreamTitle';
 * 
 * // Inside render, if user has joined:
 * {myEntry && (
 *   <StreamTitleDisplay entry={myEntry} style={styles.streamTitleSection} />
 * )}
 * ```
 * 
 * ============================================================================
 * 5. VALIDATION EXAMPLES (COMPLIANT FORMAT)
 * ============================================================================
 * 
 * // Example outputs for different scenarios:
 * 
 * // 4-player Street Fighter 6 bracket with $5 entry
 * generateEventNames({
 *   gameKey: 'sf6',
 *   bracketSize: 4,
 *   firstPlacePrizeCents: 1800, // $18
 * });
 * // Returns:
 * // {
 * //   eventName: "Dojo Duel $18 to 1st",                    // NO game name ✅
 * //   baseStreamTitle: "CritCash SF6 Dojo Duel $18 to 1st"  // Has abbrev ✅
 * // }
 * 
 * // 16-player Madden bracket with $10 entry
 * generateEventNames({
 *   gameKey: 'mdn',
 *   bracketSize: 16,
 *   firstPlacePrizeCents: 14400, // $144
 * });
 * // Returns:
 * // {
 * //   eventName: "Playoff Open $144 to 1st",                    // NO game name ✅
 * //   baseStreamTitle: "CritCash MDN Playoff Open $144 to 1st"  // Has abbrev ✅
 * // }
 * 
 * // 64-player Tekken 8 bracket with $50 entry
 * generateEventNames({
 *   gameKey: 'tk8',
 *   bracketSize: 64,
 *   firstPlacePrizeCents: 288000, // $2,880
 * });
 * // Returns:
 * // {
 * //   eventName: "Major Ring Qualifier $2.8K to 1st",                    // NO game name ✅
 * //   baseStreamTitle: "CritCash TK8 Major Ring Qualifier $2.8K to 1st"  // Has abbrev ✅
 * // }
 */

export default {
  generateEventNames,
  generateUserStreamTitle,
  getBracketMeta,
};